import React from 'react';
import { Drawer, Form, Input, Button, Select, Row, Col, message, Switch, Spin } from 'antd';
import withStyles from 'react-jss';
import { StylesProps } from 'src/library/types';
import { useDebounce } from 'src/library/hooks/useDebounce';
import { AddUserDrawerStyles } from './AddUserDrawer.styles';
import {
  createVendorUser,
  updateVendorUser,
  getRolesAndPermissions,
  searchVendorHubs,
  HubRef,
} from './UserManagement.api';
import { isHubPickerApplicable } from './UserManagement.constants';

interface IProps extends StylesProps<ReturnType<typeof AddUserDrawerStyles>> {
  isVisible: boolean;
  handleClose: () => void;
  t: (key: string) => string;
  onSuccess: () => void;
  user?: {
    userCode: string;
    name: string;
    loginUsername: string;
    phoneNumber: string;
    city: string;
    roles?: string[];
    hubs?: HubRef[];
    hubIds?: string[];
    id?: string;
    status?: string;
  } | null;
}

const AddUserDrawer = (props: IProps): React.ReactElement => {
  const { classes, isVisible, handleClose, t, user, onSuccess } = props;
  const [form] = Form.useForm();
  const [vendorRoles, setVendorRoles] = React.useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);
  const [hubOptions, setHubOptions] = React.useState<HubRef[]>([]);
  const [hubSearchString, setHubSearchString] = React.useState('');
  const [hubSearchLoading, setHubSearchLoading] = React.useState(false);
  const debouncedHubSearch = useDebounce(hubSearchString, 300);

  const loggedInUserId = window.localStorage.getItem('vendorUserId');
  const isEditingSelf = user && user.id && user.id === loggedInUserId;
  const showHubPicker = isHubPickerApplicable(selectedRoles);

  React.useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { VENDOR_ROLES } = await getRolesAndPermissions();
        setVendorRoles(VENDOR_ROLES);
      } catch (error) {
        message.error(t('Failed to fetch roles. Please try again.'));
      }
    };

    if (isVisible) {
      fetchRoles();
      if (user) {
        const initialHubs: HubRef[] = user.hubs || [];
        const initialRoles = user.roles || [];
        setHubOptions(initialHubs);
        setSelectedRoles(initialRoles);
        form.setFieldsValue({
          userCode: user.userCode,
          name: user.name,
          email: user.loginUsername,
          mobile: user.phoneNumber,
          city: user.city,
          roles: initialRoles,
          status: user.status ? user.status.toLowerCase() === 'active' : true,
          hubIds: initialHubs.length
            ? initialHubs.map((h) => h.id)
            : (user.hubIds || []),
        });
      } else {
        setHubOptions([]);
        setSelectedRoles([]);
        setHubSearchString('');
        form.resetFields();
      }
    }
  }, [user, form, isVisible]);

  React.useEffect(() => {
    if (!isVisible || !showHubPicker) {
      return undefined;
    }
    let cancelled = false;
    const runSearch = async () => {
      setHubSearchLoading(true);
      const response = await searchVendorHubs(debouncedHubSearch || '');
      if (cancelled) {
        return;
      }
      if (response.isSuccess && Array.isArray(response.data)) {
        const fetched: HubRef[] = response.data.map((h: any) => ({
          id: String(h.id),
          code: h.code,
          name: h.name,
        }));
        // Keep currently-selected hubs visible (so their chips render) and overlay the
        // latest search results. Drops stale results from previous searches.
        setHubOptions((prev) => {
          const selectedIds = new Set<string>(
            (form.getFieldValue('hubIds') || []).map((id: any) => String(id)),
          );
          const merged = new Map<string, HubRef>();
          prev.filter((h) => selectedIds.has(h.id)).forEach((h) => merged.set(h.id, h));
          fetched.forEach((h) => merged.set(h.id, h));
          return Array.from(merged.values());
        });
      }
      setHubSearchLoading(false);
    };
    runSearch();
    return () => {
      cancelled = true;
    };
  }, [debouncedHubSearch, isVisible, showHubPicker, form]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        let statusValue = 'active';
        if (user) {
          statusValue = values.status ? 'active' : 'inactive';
        }
        const payload: any = {
          username: values.userCode,
          email: values.email,
          name: values.name,
          phone: values.mobile,
          city: values.city,
          status: statusValue,
          user_roles: values.roles,
        };
        if (isHubPickerApplicable(values.roles || [])) {
          payload.hub_ids = values.hubIds || [];
        } else {
          payload.hub_ids = [];
        }
        let response;
        if (user) {
          if (values.password) {
            payload.password = values.password;
          }
          response = await updateVendorUser(payload);
        } else {
          payload.password = values.password;
          response = await createVendorUser(payload);
        }
        if (response.isSuccess) {
          message.success(user ? 'User updated successfully' : 'User created successfully');
          form.resetFields();
          handleClose();
          onSuccess();
        } else {
          message.error(
            response.errorMessage || (user ? 'Failed to update user' : 'Failed to create user'),
          );
        }
      })
      .catch(() => null);
  };

  return (
    <Drawer
      title={user ? t('Edit User') : t('Add User')}
      width={480}
      visible={isVisible}
      onClose={handleClose}
      destroyOnClose
    >
      <div className={classes.drawerContent}>
        <div className={classes.sectionTitle}>{t('User Details')}</div>
        <Form layout="vertical" form={form}>
          <Form.Item
            label={t('Username')}
            name="userCode"
            rules={[{ required: true, message: t('Please input Username') }]}
          >
            <Input placeholder={t('Username')} disabled={!!user} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('Name')}
                name="name"
                rules={[{ required: true, message: t('Please input Name') }]}
              >
                <Input placeholder={t('Name')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('Email')}
                name="email"
                rules={[{ required: true, type: 'email', message: t('Please input Email') }]}
              >
                <Input placeholder={t('Email')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('Mobile No')}
                name="mobile"
                rules={[{ required: true, message: t('Please input Mobile Number') }]}
              >
                <Input placeholder={t('Enter 10 Digit Phone Number')} maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('City')}
                name="city"
                rules={[{ required: true, message: t('Please input City') }]}
              >
                <Input placeholder={t('City')} />
              </Form.Item>
            </Col>
          </Row>
          {user && (
            <Form.Item
              label={t('Status')}
              name="status"
              valuePropName="checked"
              tooltip={isEditingSelf ? t('You cannot deactivate your own account') : undefined}
            >
              <Switch
                checkedChildren={t('Active')}
                unCheckedChildren={t('Inactive')}
                disabled={isEditingSelf}
              />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('Password')}
                name="password"
                rules={
                  user
                    ? []
                    : [{ required: true, message: t('Please input Password') }]
                }
              >
                <Input.Password placeholder={t('Password')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('Confirm Password')}
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  ...(!user
                    ? [{ required: true, message: t('Please confirm password') }]
                    : []),
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const password = getFieldValue('password');
                      if (!password && !value) {
                        return Promise.resolve();
                      }
                      if (password && !value) {
                        return Promise.reject(new Error(t('Please confirm password')));
                      }
                      if (password === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('Passwords do not match')));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder={t('Confirm Password')} />
              </Form.Item>
            </Col>
          </Row>
          <div className={classes.sectionTitle}>{t('Assign Roles')}</div>
          <Form.Item
            label={t('User Roles')}
            name="roles"
            rules={[{ required: true, message: t('Please select roles') }]}
          >
            <Select
              placeholder={t('Select Role')}
              mode="multiple"
              onChange={(vals: string[]) => setSelectedRoles(vals || [])}
            >
              {vendorRoles.map((role) => (
                <Select.Option key={role} value={role}>
                  {role}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {showHubPicker && (
            <>
              <div className={classes.sectionTitle}>{t('Assign Hubs')}</div>
              <Form.Item
                label={t('Hubs')}
                name="hubIds"
                extra={t('Leave empty to grant access to all hubs')}
              >
                <Select
                  placeholder={t('Search & select hubs')}
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  notFoundContent={hubSearchLoading ? <Spin size="small" /> : null}
                  onSearch={(value) => setHubSearchString(value)}
                >
                  {hubOptions.map((h) => (
                    <Select.Option key={h.id} value={h.id}>
                      {h.code}
                      {h.name ? ` — ${h.name}` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          <div className={classes.buttonRow}>
            <Button onClick={handleClose}>{t('Cancel')}</Button>
            <Button type="primary" onClick={handleSubmit}>
              {t('Submit')}
            </Button>
          </div>
        </Form>
      </div>
    </Drawer>
  );
};

export default withStyles(AddUserDrawerStyles)(AddUserDrawer);
